<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="flex shrink-0 gap-2 rounded-t border-x border-t p-2">
      <div class="flex-1">
        <BaseSearchInput v-model="search_term" />
      </div>
      <Popover v-slot="{ open }" class="relative">
        <Float
          :auto-placement="{
            autoAlignment: true,
            allowedPlacements: ['bottom-end', 'top-end']
          }"
          portal
          :offset="10"
          transition-name="slide-fade-from-bottom"
        >
          <PopoverButton :class="open ? '' : 'text-opacity-90'">
            <BaseButton icon="sliders-up" />
          </PopoverButton>
          <Transition
            enter-active-class="transform duration-200 ease-out"
            enter-from-class="translate-y-1 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transform duration-150 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-1 opacity-0"
          >
            <PopoverPanel
              v-slot="{ close }"
              static
              class="z-100 max-w-sm px-4 sm:max-w-xl sm:px-0"
            >
              <div
                class="rounded border border-gray-300 bg-white shadow-md dark:bg-gray-700"
              >
                <div
                  class="flex w-full flex-row gap-2 p-4 dark:border-slate-700"
                >
                  <div>
                    <SelectComponent
                      v-model="filter.funeral_home"
                      :edition-mode="true"
                      name="funeral_home"
                      class="w-64"
                      :label="$t('attributes.funeral_home')"
                      :values="initialMetadata.models.funeralHomes"
                      multiple
                    />
                  </div>
                  <div v-if="display === 'users'">
                    <SelectComponent
                      v-model="filter.function"
                      :edition-mode="true"
                      :label="$t('global.users_function')"
                      name=""
                      class="w-64"
                      :values="filteredRoles"
                      multiple
                    />
                  </div>
                  <div v-else-if="display === 'material'">
                    <SelectComponent
                      v-model="filter.material_resource_type"
                      :edition-mode="true"
                      :label="$t('global.resources_type')"
                      name=""
                      class="w-64"
                      :values="
                        initialMetadata?.models?.list_items
                          ?.material_resources_types ?? []
                      "
                      multiple
                    />
                  </div>
                </div>
              </div>
            </PopoverPanel>
          </Transition>
        </Float>
      </Popover>
      <!--      </template>-->

      <div class="flex-shrink-0">
        <WalkmanButtonIcon
          v-model="display"
          :options="[
            { value: 'users', icon: ['fal', 'users'] },
            { value: 'material', icon: ['fas', 'drone'] }
          ]"
        />
      </div>
    </div>
    <div class="flex-1 overflow-y-auto">
      <AssignablePeopleList
        v-if="display === 'users'"
        :assignable-people="assignablePeopleFiltered"
        :with-availability="true"
        :all-availability-items="allUsersAvailabilityItems"
        @fetch-events="$emit('fetch-events')"
      />
      <AssignableResourcesList
        v-else-if="display === 'material'"
        :assignable-material-resources="assignableMaterialResourcesFiltered"
        :with-availability="true"
        :all-availability-items="allMaterialResourcesAvailabilityItems"
        @fetch-events="$emit('fetch-events')"
      />
    </div>
  </div>
</template>

<script>
import AssignablePeopleList from '@c/Dashboard/Content/Planning/components/AssignablePeopleList.vue'
import WalkmanButtonIcon from '@c/WalkmanButtonIcon.vue'
import BaseSearchInput from '@c/addf-package/components/BaseInput/BaseSearchInput.vue'
import AssignableResourcesList from '@c/Dashboard/Content/Planning/components/AssignableResourcesList.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { Float } from '@headlessui-float/vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import { mapGetters } from 'vuex'
import persistViewFiltersStateMixin from '@/mixins/persistViewFiltersStateMixin'

export default {
  name: 'AssignableContainer',
  components: {
    SelectComponent,
    Popover,
    PopoverPanel,
    PopoverButton,
    Float,
    BaseButton,
    AssignableResourcesList,
    BaseSearchInput,
    WalkmanButtonIcon,
    AssignablePeopleList
  },
  mixins: [persistViewFiltersStateMixin],
  props: {
    assignablePeople: {
      type: Array,
      required: true
    },
    assignableMaterialResources: {
      type: Array,
      required: true
    },
    allUsersAvailabilityItems: {
      type: Array,
      required: false,
      default: () => []
    },
    allMaterialResourcesAvailabilityItems: {
      type: Array,
      required: false,
      default: () => []
    },
    currentDashboard: {
      type: Object,
      required: false,
      default: () => {}
    }
  },
  emits: ['fetch-events'],
  persistViewState: ['search_term'],
  data() {
    return {
      display: 'users',
      search_term: '',
      isPopoverOpen: false,
      filter: this.getFilter()
    }
  },
  computed: {
    ...mapGetters({
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    filteredRoles() {
      return this.initialMetadata?.models?.roles_for_filter ?? []
    },
    assignablePeopleFiltered() {
      if (this.display !== 'users') return this.assignablePeople

      return this.assignablePeople
        .when(this.search_term.length > 2, (assignable_people) => {
          const search_term = this.$h.normalizeSearch(this.search_term)
          return assignable_people.filter((assignable_person) => {
            return this.$h
              .normalizeSearch(assignable_person.user.full_name)
              .includes(search_term)
          })
        })
        .when(
          this.filter?.funeral_home && this.filter.funeral_home.length,
          (assignable_people) =>
            assignable_people.filter((person) =>
              person.user.funeral_homes_id.some((id) =>
                this.filter.funeral_home.includes(id)
              )
            )
        )
        .when(
          this.filter?.function && this.filter.function.length,
          (assignable_people) =>
            assignable_people.filter((person) =>
              person.user.roles.some((role) =>
                this.filter.function.includes(role)
              )
            )
        )
        .sort((a, b) => a.user.full_name.localeCompare(b.user.full_name))
    },

    assignableMaterialResourcesFiltered() {
      if (this.display !== 'material') return this.assignableMaterialResources

      let filtered_material_resources = this.assignableMaterialResources

      if (this.search_term.length > 2) {
        const search_term = this.$h.normalizeSearch(this.search_term)
        filtered_material_resources = filtered_material_resources.filter(
          (event) =>
            this.$h
              .normalizeSearch(event.material_resource.name_index)
              .includes(search_term)
        )
      }

      if (this.filter?.funeral_home && this.filter.funeral_home.length) {
        filtered_material_resources = filtered_material_resources.filter(
          (event) =>
            event.material_resource.funeral_homes_id.some((id) =>
              this.filter.funeral_home.includes(id)
            )
        )
      }

      if (
        this.filter?.material_resource_type &&
        this.filter.material_resource_type.length
      ) {
        filtered_material_resources = filtered_material_resources.filter(
          (filtered_material_resource) => {
            return this.filter.material_resource_type.includes(
              filtered_material_resource.material_resource.type
            )
          }
        )
      }

      return filtered_material_resources
    }
  },
  watch: {
    filter: {
      handler(newFilter) {
        localStorage.setItem(
          `${this.$route.params.id}-filter-resource-planning`,
          JSON.stringify(newFilter)
        )
      },
      deep: true
    }
  },
  methods: {
    getFilter() {
      let ls = localStorage.getItem(
        `${this.$route.params.id}-filter-resource-planning`
      )
      if (ls) {
        let parsed = JSON.parse(ls)
        if (!Array.isArray(parsed.function)) {
          parsed.function = [parsed.function]
        }
        if (!Array.isArray(parsed.material_resource_type)) {
          parsed.material_resource_type = [parsed.material_resource_type]
        }
        return JSON.parse(ls)
      }
      return {
        function: [],
        material_resource_type: [],
        funeral_home: []
      }
    }
  }
}
</script>
